import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Select,
  MenuItem,
  FormControl,
  TableSortLabel,
  Tooltip
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useDateSort } from '../../../../hooks/useDateSort';
import TableActionsMenu from '../../../../components/common/TableActionsMenu';
import AdpopcornMediationDeltaTop5 from '../avatar/AdpopcornMediationDeltaTop5';
import { AdpopcornMediationDetailMetrics } from '../../../../types/adpopcorn.types';
import type { Dayjs } from 'dayjs';

type Props = {
  data: AdpopcornMediationDetailMetrics;
  thirdPartyId?: number;
  thirdPartyName?: string;
  selectedApp?: string | null;
  startDate: Dayjs;
  endDate: Dayjs;
  onThirdPartyIdChange?: (thirdPartyId: number, thirdPartyName: string) => void;
};

// key: thirdPartyId
const MediationOptions: Record<number, string> = {
  28: 'ADforus',
  1: 'AdMob',
  86: 'ADOP',
  0: 'AdPopcorn',
  23: 'AppLovin (Bidding)',
  21: 'AppLovin (Waterfall)',
  4: 'Cauly',
  16: 'Fyber',
  19: 'GAM (Google Ad Manager)',
  5: 'Mezzomedia',
  6: 'Mobon',
  26: 'MobWith',
  18: 'Pangle',
  7: 'Unity',
  14: 'Vungle'
};

export default function AdpopcornMediationDetailMetricsTable({
  data,
  thirdPartyId = -1,
  thirdPartyName = '미디에이션 전체',
  selectedApp,
  startDate,
  endDate,
  onThirdPartyIdChange
}: Props) {
  const [selectedMediationId, setSelectedMediationId] = useState<number>(thirdPartyId);
  
  // 부모 컴포넌트의 thirdPartyId가 변경되면 동기화
  useEffect(() => {
    setSelectedMediationId(thirdPartyId);
  }, [thirdPartyId]);
  const top5MediationStats = data.top5MediationStats;
  const bottom5MediationStats = data.bottom5MediationStats;
  const mediationDetailStats = data.mediationDetailStats;
  const rowsPerPage = 31;
  const [page, setPage] = useState(0);
  
  // 테이블 ref - 엑셀 다운로드용
  const tableRef = useRef<HTMLTableElement>(null);
  
  const handleMediationChange = (value: number) => {
    setSelectedMediationId(value);
    const name = value === -1 ? '미디에이션 전체' : (MediationOptions[value] ?? '미디에이션 전체');
    if (onThirdPartyIdChange) {
      onThirdPartyIdChange(value, name);
    }
  };

  const { sorted: sortedData, sortDirection, toggleSort } = useDateSort(mediationDetailStats);

  // 파일명 생성
  const filename = useMemo(() => {
    const appPart = selectedApp && selectedApp !== '앱 전체' ? `_${selectedApp}` : '_전체';
    return `${startDate.format('YYMMDD')}-${endDate.format('YYMMDD')}${appPart}`;
  }, [startDate, endDate, selectedApp]);

  const formatCurrency = useCallback((value: number) => {
    return new Intl.NumberFormat('ko-KR', {
      style: 'currency',
      currency: 'KRW'
    }).format(value);
  }, []);
  const formatNumberOnly = (value: number) => Math.round(value).toLocaleString('ko-KR');

  // baseSorted는 항상 내림차순: [최신날짜, ..., 오래된날짜]
  // 정렬 방향과 무관하게 baseSorted 기준으로 증감률 계산
  const baseSorted = useMemo(() => {
    const copied = [...mediationDetailStats];
    copied.sort((a, b) => b.date.localeCompare(a.date)); // 내림차순
    return copied;
  }, [mediationDetailStats]);

  // 증감률을 baseSorted 기준으로 한 번만 계산하고 저장
  const revenueDeltaMap = useMemo(() => {
    const map = new Map<string, React.ReactNode>();
    baseSorted.forEach((row, idx) => {
      if (idx === baseSorted.length - 1) {
        map.set(row.date, '-');
        return;
      }
      const prevRow = baseSorted[idx + 1];
      if (!prevRow) {
        map.set(row.date, '-');
        return;
      }

      const delta = row.revenue - prevRow.revenue;
      const rawDeltaPercentage =
        prevRow.revenue > 0 ? Math.round((delta / prevRow.revenue) * 100) : 0;
      const deltaPercentage = rawDeltaPercentage.toLocaleString('ko-KR');

      const isPositive = delta >= 0;
      const arrow = isPositive ? '▲' : '▼';
      const sign = isPositive ? '+' : '';

      map.set(
        row.date,
        <Box
          component="span"
          sx={{
            color: isPositive ? '#D32F2F' : '#1976D2',
            display: 'inline-flex',
            alignItems: 'center'
          }}
        >
          {arrow} {deltaPercentage}% ({sign}
          {formatCurrency(delta)})
        </Box>
      );
    });
    return map;
  }, [baseSorted, formatCurrency]);

  const paged = useMemo(
    () => sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [sortedData, page]
  );

  const calculateFillRate = (req: number, imp: number) => {
    return req > 0 ? ((imp / req) * 100).toFixed(2) : '0.00';
  };

  const calculateCTR = (imp: number, clk: number) => {
    return imp > 0 ? ((clk / imp) * 100).toFixed(2) : '0.00';
  };

  const calculateECPM = (revenue: number, imp: number) => {
    return imp > 0 ? formatCurrency((revenue / imp) * 1000) : formatCurrency(0);
  };

  const average = (vals: Array<number | undefined>) => {
    const nums = vals.filter((v): v is number => v !== undefined);
    return nums.length ? nums.reduce((s, v) => s + v, 0) / nums.length : 0;
  };

  // 합계 계산 (평균: fill rate/CTR/eCPM, 합계: 총 매출/request/response/impression/click)
  const totals = useMemo(() => {
    const totalRevenue = sortedData.reduce((sum, row) => sum + row.revenue, 0);
    const totalReq = sortedData.reduce((sum, row) => sum + row.req, 0);
    const totalRes = sortedData.reduce((sum, row) => sum + row.res, 0);
    const totalImp = sortedData.reduce((sum, row) => sum + row.imp, 0);
    const totalClk = sortedData.reduce((sum, row) => sum + row.clk, 0);
    
    // 행별 비율을 산술평균으로 계산 (유효 값만 포함)
    const avgFillRate = average(
      sortedData.map((row) =>
        row.req > 0 ? (row.imp / row.req) * 100 : undefined
      )
    ).toFixed(2);

    const avgCTR = average(
      sortedData.map((row) =>
        row.imp > 0 && row.clk <= row.imp ? (row.clk / row.imp) * 100 : undefined
      )
    ).toFixed(2);

    const avgECPM = formatCurrency(
      average(
        sortedData.map((row) =>
          row.imp > 0 ? (row.revenue / row.imp) * 1000 : undefined
        )
      )
    );
    
    return {
      totalRevenue,
      totalReq,
      totalRes,
      totalImp,
      totalClk,
      avgFillRate,
      avgCTR,
      avgECPM
    };
  }, [sortedData, formatCurrency]);

  // 엑셀 다운로드용 전체 데이터 (현재 정렬 상태 그대로, 페이지네이션 무시)
  const allRowsDataForExcel = useMemo(() => {
    return sortedData.map((row) => {
      // revenueDeltaMap에서 텍스트 추출 (ReactNode를 문자열로 변환)
      const revenueDeltaNode = revenueDeltaMap.get(row.date);
      let revenueDeltaText = '-';
      
      if (revenueDeltaNode && revenueDeltaNode !== '-') {
        // ReactNode를 문자열로 변환하기 위해 임시로 렌더링
        // 하지만 더 간단하게 baseSorted 기준으로 직접 계산
        const baseIndex = baseSorted.findIndex(item => item.date === row.date);
        if (baseIndex !== -1 && baseIndex < baseSorted.length - 1) {
          const prevRow = baseSorted[baseIndex + 1];
          if (prevRow) {
            const delta = row.revenue - prevRow.revenue;
            const rawDeltaPercentage = prevRow.revenue > 0 ? Math.round((delta / prevRow.revenue) * 100) : 0;
            const arrow = delta >= 0 ? '▲' : '▼';
            const deltaPercentageAbs = Math.abs(rawDeltaPercentage).toLocaleString('ko-KR');
            const deltaAmountAbs = Math.abs(delta).toLocaleString('ko-KR');
            const sign = delta >= 0 ? '+' : '-';
            revenueDeltaText = `${arrow} ${deltaPercentageAbs}% (${sign}${deltaAmountAbs})`;
          }
        }
      }

      // 퍼센트 컬럼은 소수로 전달
      const fillRate = row.req > 0 ? row.imp / row.req : 0;
      const ctr = row.imp > 0 ? row.clk / row.imp : 0;

      return [
        row.date,
        formatNumberOnly(row.revenue),
        revenueDeltaText,
        row.req.toLocaleString(),
        row.res.toLocaleString(),
        fillRate,
        row.imp.toLocaleString(),
        row.clk.toLocaleString(),
        ctr,
        formatNumberOnly((row.revenue / Math.max(row.imp, 1)) * 1000)
      ];
    });
  }, [sortedData, baseSorted, revenueDeltaMap]);

  return (
    <Box>
      <AdpopcornMediationDeltaTop5
        top5MediationStats={top5MediationStats}
        bottom5MediationStats={bottom5MediationStats}
      />

      {/* 미디에이션 선택 영역 */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          mt: 4,
          ml: 2,
          gap: 2
        }}
      >
        <Typography variant="body1" fontWeight="bold">
          미디에이션 선택
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            p: 1.5,
            border: '1px solid #e0e0e0',
            borderRadius: 2,
            bgcolor: '#f5f5f5',
            minWidth: 300,
            maxWidth: 400
          }}
        >
          <SearchIcon sx={{ color: '#666', mr: 1 }} />
          <FormControl sx={{ flex: 1 }} variant="standard">
            <Select
              value={selectedMediationId}
              onChange={(e) => handleMediationChange(Number(e.target.value))}
              displayEmpty
              renderValue={(value) => {
                if (value === -1) {
                  return '미디에이션 전체';
                }
                return MediationOptions[value as number] ?? '미디에이션 전체';
              }}
              disableUnderline
              IconComponent={KeyboardArrowDownIcon}
              inputProps={{
                'aria-label': '미디에이션 선택'
              }}
              sx={{
                '& .MuiSelect-select': {
                  padding: 0,
                  paddingRight: '24px !important',
                  display: 'flex',
                  alignItems: 'center'
                },
                '& .MuiSelect-icon': {
                  color: '#666'
                }
              }}
            >
              <MenuItem value={-1}>미디에이션 전체</MenuItem>
              {Object.entries(MediationOptions).map(([id, name]) => (
                <MenuItem key={id} value={Number(id)}>
                  {name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, mb: 1 }}>
        <TableActionsMenu 
          tableRef={tableRef} 
          allRowsData={allRowsDataForExcel}
          filename={filename}
          sheetName="미디에이션상세지표"
        />
      </Box>

      {/* 테이블 */}
      <TableContainer
        sx={{
          overflowX: 'auto',
          mt: 2
        }}
      >
        <Table ref={tableRef}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>
                <TableSortLabel
                  active
                  direction={sortDirection}
                  onClick={toggleSort}
                >
                  날짜
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                총 매출
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                매출 증감률
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                Request
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                Response
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                Fill Rate
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                Impression
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                Click
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                CTR
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                eCPM
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {/* 합계 행 */}
            <TableRow sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
              <TableCell sx={{ fontWeight: 'bold' }}>합계</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                {formatCurrency(totals.totalRevenue)}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>-</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                {totals.totalReq.toLocaleString()}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                {totals.totalRes.toLocaleString()}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                  {totals.avgFillRate}%
                  <Tooltip
                    title={
                      <span style={{ whiteSpace: 'pre-line', display: 'block', textAlign: 'center' }}>
                        {"조회한 기간의 평균입니다\n* (노출수 / 요청수)"}
                      </span>
                    }
                    arrow
                  >
                    <InfoOutlinedIcon sx={{ fontSize: 14, color: '#666', cursor: 'help' }} />
                  </Tooltip>
                </Box>
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                {totals.totalImp.toLocaleString()}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                {totals.totalClk.toLocaleString()}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                  {totals.avgCTR}%
                  <Tooltip title="조회한 기간의 평균입니다" arrow>
                    <InfoOutlinedIcon sx={{ fontSize: 14, color: '#666', cursor: 'help' }} />
                  </Tooltip>
                </Box>
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                  {totals.avgECPM}
                  <Tooltip title="조회한 기간의 평균입니다" arrow>
                    <InfoOutlinedIcon sx={{ fontSize: 14, color: '#666', cursor: 'help' }} />
                  </Tooltip>
                </Box>
              </TableCell>
            </TableRow>
            {paged.map((row) => {
              const revenueDelta = revenueDeltaMap.get(row.date) || '-';
              const fillRate = calculateFillRate(row.req, row.imp);
              const ctr = calculateCTR(row.imp, row.clk);
              const ecpm = calculateECPM(row.revenue, row.imp);

              return (
                <TableRow key={row.date}>
                  <TableCell>{row.date}</TableCell>
                  <TableCell align="right">{formatCurrency(row.revenue)}</TableCell>
                  <TableCell align="right">{revenueDelta}</TableCell>
                  <TableCell align="right">{row.req.toLocaleString()}</TableCell>
                  <TableCell align="right">{row.res.toLocaleString()}</TableCell>
                  <TableCell align="right">{fillRate}%</TableCell>
                  <TableCell align="right">{row.imp.toLocaleString()}</TableCell>
                  <TableCell align="right">{row.clk.toLocaleString()}</TableCell>
                  <TableCell align="right">{ctr}%</TableCell>
                  <TableCell align="right">{ecpm}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={sortedData.length}
        page={page}
        onPageChange={(_, p) => setPage(p)}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[rowsPerPage]}
      />
    </Box>
  );
}
